import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push as navigateTo } from 'connected-react-router';
import { useTranslation } from 'react-i18next';
import { State } from 'reducers';
import { getBuildingByUrlSlug, getBuildingThemes, HasUrlSlug } from 'selectors/buildingsSelectors';
import { deleteThemeSuccess, publishThemeRequest, resetThemeStatusRequest } from 'actions/themeActions';
import { getBuildingThemesRequest } from 'actions/buildingActions';
import { ThemeList } from '../themeList/ThemeList';
import { Theme, ThemeId } from 'shared/models';
import { getDeviceTypes, getIsLoadingDeviceTypes } from 'selectors/deviceTypesSelectors';
import { Route, useLocation } from 'react-router-dom';
import { ScheduleThemeDialog } from '../../schedules/scheduleThemeDialog/ScheduleThemeDialog';
import { CopyThemeDialog } from '../copyThemeDialog/CopyThemeDialog';
import { NewThemeDialog } from '../newThemeDialog/NewThemeDialog';
import { DebugLink } from 'ui/shared/DebugLink';
import { useParams } from 'util/reactRouterHooks';
import { getDeviceTypesRequest } from 'actions/deviceTypeActions';
import { useSession } from 'hooks';
import { Permission } from 'shared/permissions';
import { analytics } from 'util/analytics';
import { toError } from 'shared/util/toError';
import { callApi } from 'apiClient';
import { ErrorMessage, InfoMessage } from 'ui/shared/messages';
import { useBuilding } from 'hooks/useBuilding/useBuilding';
import { ExportThemeDialog } from 'ui/editor/exportThemeDialog/ExportThemeDialog';
import { Loading } from 'ui/shared/loading';

require('./BuildingThemesPage.css');

export const BuildingThemesPage = () => {
    const { t } = useTranslation();
    const params = useParams<HasUrlSlug>();
    const location = useLocation<HasUrlSlug>();
    const { urlSlug } = params;

    const { hasPermission, user } = useSession();

    const building = useSelector((state: State) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id;

    // When an Infotainment Org is present, a User is unable to edit Themes unless
    // he/she belongs to that Org, has AdminTheme permissions in that Building,
    // or has AdminTheme and access to all Buildings.
    const { infotainmentOrg } = useBuilding(buildingId, true);
    const isLockedOut =
        infotainmentOrg
            ? !(
                (user?.orgId === infotainmentOrg?.orgId) ||
                hasPermission(buildingId, Permission.AdminTheme) || (
                    hasPermission(null, Permission.AdminTheme) &&
                    hasPermission(null, Permission.AllBuildings)
                )
            )
            : false;

    const themes = useSelector((state: State) => buildingId ? getBuildingThemes(state, { buildingId }) : null);

    const [ errorMessage, setErrorMessage ] = useState('');
    const [ exportDialogVisible, setExportDialogVisible ] = useState(false);
    const [ elevatorId, setElevatorId ] = useState('');
    const [ currentTheme, setCurrentTheme ] = useState<Theme | null>(null);

    const deviceTypes = useSelector(getDeviceTypes);
    const isLoadingDeviceTypes = useSelector(getIsLoadingDeviceTypes);

    const dispatch = useDispatch();

    const navigateToSubRoute = (path: string) =>
        (themeId: ThemeId) => () => dispatch(navigateTo(`/buildings/${urlSlug}/themes/${themeId}/${path}`))
    ;

    const handleClick = (themeId: ThemeId) => () => dispatch(navigateTo(`/buildings/${urlSlug}/themes/${themeId}/edit`));
    const onClickCreate = () => dispatch(navigateTo(`/buildings/${urlSlug}/themes/new`));
    const handleClickPublish = (themeId: ThemeId) => () => dispatch(publishThemeRequest(themeId, { buildingId: buildingId! }));
    const handleClickExport = (themeId: ThemeId) => () => {
        setCurrentTheme(themes ? themes[themeId] : null);
        setExportDialogVisible(true);
    };
    const handleClickDownload = (url: string | null) => () => {
        if (url) {
            window.open(`${url}?${Math.random().toString(36).substring(2, 5)}&preview`, "_blank");
        }
    };
    
    const handleClickReset = (themeId: ThemeId) => () => dispatch(resetThemeStatusRequest(themeId));
    const handleClickSchedule = navigateToSubRoute('schedule');
    const handleClickCopy = navigateToSubRoute('copy');
    const handleClickDelete = useCallback((themeId: ThemeId) => () => {
        setErrorMessage('');
        void (async () => {
            try {
                await callApi('DELETE', `/api/themes/${themeId}`);
                dispatch(deleteThemeSuccess(themeId));
            }
            catch (e) {
                const err = toError(e);
                setErrorMessage(t([ `THEMES.ERROR.${err.message}`, 'SERVER_ERROR' ]));
            }
        })();
    }, [ dispatch, setErrorMessage, t ]);

    const isBuildingUserOrManager = hasPermission(buildingId, Permission.ViewThemes) && !hasPermission(null, Permission.AllBuildings);
    const isFloatingUser = user && !user.orgId;
    const isNonEcs = building && !building.hasEcs;
    const isNorthAmerica = !!(building?.country === 'US' || building?.country === 'CA');
    const isEligibleForDesignServices = !!(isBuildingUserOrManager && isFloatingUser && isNonEcs && isNorthAmerica);

    useEffect(() => {
        if ((!deviceTypes || !Object.keys(deviceTypes).length) && !isLoadingDeviceTypes) {
            dispatch(getDeviceTypesRequest());
        }
    }, [ dispatch, deviceTypes, isLoadingDeviceTypes ]);

    useEffect(() => {
        if (buildingId) {
            dispatch(getBuildingThemesRequest(buildingId));
        }
    }, [ dispatch, buildingId ]);

    if (!buildingId) {
        return (
            <Loading/>
        );
    }

    return (
        <div id="building-themes-page" className="page">
            <div className="_top">
                <button
                    id="create-theme"
                    type="button"
                    className="ui basic button"
                    onClick={ onClickCreate }
                    disabled={ isLockedOut || !hasPermission(buildingId, Permission.CreateTheme) }
                >
                    { t('COMMAND.CREATE_THEME') }
                </button>
                <DebugLink
                    sql={
                        `--[Building Themes]\n` +
                        `SELECT display_name theme_name, width, height, options, uploaded, etag, requirements, created, published, status, to_char((size / 1024), 'FM9,999,999,999') || ' KB' size\n` +
                        `FROM themes t INNER JOIN building_theme bt ON bt.theme_id = t.id\n` +
                        `WHERE bt.building_id = '${buildingId}'\n` +
                        `ORDER BY display_name;\n`
                    }
                />

                { isEligibleForDesignServices &&
                    <span className="_ds">
                        Screen design services available.
                        <a
                            href="https://madelevator.com/company/design-services"
                            target="_blank"
                            rel="noreferrer"
                            onClick={ () => analytics.event('Design Services', 'click', 'Learn More inline link') }
                        >
                            Learn more
                        </a>
                        .
                    </span>
                }
            </div>
            
            { infotainmentOrg &&
                <>
                    <InfoMessage message={ t('BUILDING.THEMES_MANAGED_BY', [ infotainmentOrg.displayName ]) }/>
                    <span className="_vc-info">
                        <a href="https://www.verticalcity.com/register">Register / Opt Out</a> or call <a href="tel:18667893776">1-866-789-3776</a>
                    </span>
                </>
            }

            <ThemeList
                isLockedOut={ isLockedOut }
                buildingId={ buildingId }
                themes={ themes }
                deviceTypes={ deviceTypes }
                onClick={ handleClick }
                onClickEdit={ handleClick }
                onClickPublish={ handleClickPublish }
                onClickExport={ handleClickExport }
                onClickDownload={ handleClickDownload }
                onClickReset={ handleClickReset }
                onClickSchedule={ handleClickSchedule }
                onClickCopy={ handleClickCopy }
                onClickDelete={ handleClickDelete }
            />

            <ExportThemeDialog
                theme={ currentTheme }
                buildingId={ buildingId }
                elevatorId={ elevatorId }
                onChangeElevatorId={ setElevatorId }
                visible={ exportDialogVisible }
                onClose={ () => setExportDialogVisible(false) }
            />

            <ErrorMessage
                hideAfter={ 4000 }
                message={ errorMessage }
                onClick={ () => setErrorMessage('') }
            />

            <Route path="/buildings/:urlSlug/themes/:themeId">
                <NewThemeDialog
                    visible={ /\/buildings\/.+\/themes\/new$/.test(location.pathname) }
                    isNorthAmerica={ isNorthAmerica }
                />
                <CopyThemeDialog
                    visible={ /\/buildings\/.+\/themes\/.+\/copy$/.test(location.pathname) }
                />
                <ScheduleThemeDialog
                    visible={ /\/buildings\/.+\/themes\/.+\/schedule$/.test(location.pathname) }
                    onClose={ () => dispatch(navigateTo(`/buildings/${urlSlug}/themes`)) }
                />
            </Route>
        </div>
    );
};
